import React, { useMemo, useState } from 'react';
import { Card, Table, Typography, Empty, Skeleton, Input, Button, Dropdown } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useGetProductSalesBreakdownQuery } from '../../services/reportsApi';
import { useAppSelector } from '../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../utils/reportExport';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ProductAnalysisProps {
  selectedPeriod: string;
  customDateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ProductAnalysis: React.FC<ProductAnalysisProps> = ({
  selectedPeriod,
  customDateRange,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { startOfDay, startOfWeek, startOfMonth, formatInTimeZone } = useRestaurantTimeZone();
  const [searchText, setSearchText] = useState('');
  const [activeView, setActiveView] = useState<'top' | 'bottom' | 'all'>('top');

  // Calculate date range based on selected period using restaurant timezone
  const dateRange = useMemo(() => {
    const today = startOfDay();

    if (customDateRange && customDateRange[0] && customDateRange[1]) {
      return {
        fromDate: customDateRange[0].format('YYYY-MM-DD'),
        toDate: customDateRange[1].format('YYYY-MM-DD'),
      };
    }

    switch (selectedPeriod) {
      case 'today':
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'week':
        return {
          fromDate: startOfWeek().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'month':
        return {
          fromDate: startOfMonth().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'custom':
        // If custom period is selected but no date range, return empty to skip query
        if (!customDateRange) {
          return { fromDate: '', toDate: '' };
        }
        return {
          fromDate: customDateRange[0].format('YYYY-MM-DD'),
          toDate: customDateRange[1].format('YYYY-MM-DD'),
        };
      default:
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
    }
  }, [selectedPeriod, customDateRange, startOfDay, startOfWeek, startOfMonth]);

  const { data: products, isLoading, error } = useGetProductSalesBreakdownQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!searchText) return products;

    const search = searchText.toLowerCase();
    return products.filter(
      (p) =>
        p.productName.toLowerCase().includes(search) ||
        p.categoryName.toLowerCase().includes(search)
    );
  }, [products, searchText]);

  // Split products into top and bottom performers
  const topProducts = useMemo(() => {
    return filteredProducts.slice(0, 10);
  }, [filteredProducts]);

  const bottomProducts = useMemo(() => {
    return filteredProducts.slice(-10).reverse();
  }, [filteredProducts]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!products || products.length === 0) {
      return {
        totalProducts: 0,
        totalQuantity: 0,
        avgPricePerUnit: 0,
        topProductName: '',
        topProductQuantity: 0,
      };
    }

    const totalRevenue = products.reduce((sum, p) => sum + p.revenue, 0);
    const totalQuantity = products.reduce((sum, p) => sum + p.quantitySold, 0);

    return {
      totalProducts: products.length,
      totalQuantity,
      avgPricePerUnit: totalQuantity > 0 ? totalRevenue / totalQuantity : 0,
      topProductName: products[0]?.productName || '',
      topProductQuantity: products[0]?.quantitySold || 0,
    };
  }, [products]);

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!products || products.length === 0) return;

    const periodLabel = getPeriodLabel(selectedPeriod, customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Reporte de Productos',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Total Productos', value: `${stats.totalProducts}` },
        { label: 'Unidades Vendidas', value: `${stats.totalQuantity}` },
        { label: 'Precio Prom. por Unidad', value: `${currencySymbol} ${stats.avgPricePerUnit.toFixed(2)}` },
        { label: 'Producto Estrella', value: `${stats.topProductName} (${stats.topProductQuantity} uds)` },
      ],
      sections: [{
        title: 'Productos',
        titleStyle: 'section',
        headers: ['#', 'Producto', 'Categoría', 'Cantidad', 'Ingresos', 'Precio Unit.', '%'],
        rows: products.map((p, i) => [
          i + 1, p.productName, p.categoryName, p.quantitySold,
          `${currencySymbol} ${p.revenue.toFixed(2)}`, `${currencySymbol} ${p.unitPrice.toFixed(2)}`,
          `${p.percentage.toFixed(1)}%`,
        ]),
      }],
    };

    if (format === 'csv') exportCSV(config, 'productos');
    else if (format === 'excel') exportExcel(config, 'productos');
    else exportPDF(config);
  };

  // Values wear ink with tabular figures; revenue gets a proportion bar so
  // the ranking reads at a glance (same language as the rest of Reportes).
  const maxRevenue = Math.max(...filteredProducts.map(p => p.revenue), 1);

  const columns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 44,
      render: (rank: number) => <Text type="secondary">{rank}</Text>,
    },
    {
      title: 'Producto',
      dataIndex: 'productName',
      key: 'productName',
      render: (name: string, record: any) => (
        <div>
          <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
            {record.categoryName}
          </Text>
        </div>
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantitySold',
      key: 'quantitySold',
      width: isMobile ? 80 : 100,
      align: 'center' as const,
      sorter: (a: any, b: any) => a.quantitySold - b.quantitySold,
      render: (quantity: number) => (
        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{quantity}</Text>
      ),
    },
    {
      title: 'Ingresos',
      dataIndex: 'revenue',
      key: 'revenue',
      width: isMobile ? 120 : 160,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.revenue - b.revenue,
      render: (revenue: number) => (
        <div style={{ minWidth: 110 }}>
          <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {currencySymbol} {revenue.toFixed(2)}
          </Text>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-tertiary)', marginTop: 3 }}>
            <div style={{
              height: '100%',
              width: `${(revenue / maxRevenue) * 100}%`,
              borderRadius: 2,
              background: 'var(--color-primary)',
            }} />
          </div>
        </div>
      ),
    },
    {
      title: 'Precio Prom.',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: isMobile ? 90 : 110,
      align: 'right' as const,
      render: (price: number) => (
        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
          {currencySymbol} {price.toFixed(2)}
        </Text>
      ),
    },
    {
      title: '% Ventas',
      dataIndex: 'percentage',
      key: 'percentage',
      width: isMobile ? 80 : 100,
      align: 'center' as const,
      render: (percentage: number) => (
        <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {percentage.toFixed(1)}%
        </Text>
      ),
    },
  ];

  if (error) {
    return (
      <Card>
        <Empty
          description="Error al cargar el análisis de productos"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  const kpi = (label: string, value: React.ReactNode, sub?: string) => (
    <div className="rep-kpi">
      <div className="rep-kpi__label">{label}</div>
      <div className="rep-kpi__value">{value}</div>
      {sub && <div className="rep-kpi__sub">{sub}</div>}
    </div>
  );

  // View data: top/bottom slices or the full searchable list
  const viewData =
    activeView === 'top' ? topProducts :
    activeView === 'bottom' ? bottomProducts :
    filteredProducts;

  const rankFor = (index: number) =>
    activeView === 'bottom' ? filteredProducts.length - index : index + 1;

  return (
    <div>
      {/* Views as pills + search + export in one row (no nested tab bar,
          no period strip — the page bar already says the period) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div className="rep-subpills" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className={`rep-subpill${activeView === 'top' ? ' rep-subpill--selected' : ''}`}
            onClick={() => setActiveView('top')}
          >
            Más vendidos
          </button>
          <button
            type="button"
            className={`rep-subpill${activeView === 'bottom' ? ' rep-subpill--selected' : ''}`}
            onClick={() => setActiveView('bottom')}
          >
            Menos vendidos
          </button>
          <button
            type="button"
            className={`rep-subpill${activeView === 'all' ? ' rep-subpill--selected' : ''}`}
            onClick={() => setActiveView('all')}
          >
            Todos · {filteredProducts.length}
          </button>
        </div>
        <Input
          placeholder="Buscar producto o categoría…"
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
          style={{ width: isMobile ? '100%' : 240 }}
        />
        <div style={{ flex: 1 }} />
        <Dropdown menu={{ items: [
          { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
          { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
          { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
        ]}}>
          <Button icon={<DownloadOutlined />}>Exportar</Button>
        </Dropdown>
      </div>

      <div className="rep-kpis">
        {kpi('Productos', stats.totalProducts, 'con ventas en el período')}
        {kpi('Unidades', stats.totalQuantity)}
        {kpi('Precio prom. por unidad', `${currencySymbol}${stats.avgPricePerUnit.toFixed(2)}`)}
        {kpi('Producto estrella', stats.topProductName || '—',
          stats.topProductQuantity > 0 ? `${stats.topProductQuantity} unidades` : undefined)}
      </div>

      {viewData.length === 0 ? (
        <Card>
          <Empty
            description={searchText ? 'No se encontraron productos' : 'No hay productos para mostrar'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div className="rep-panel">
          <Table
            columns={columns}
            dataSource={viewData.map((item, index) => ({
              ...item,
              key: item.productId,
              rank: rankFor(index),
            }))}
            pagination={activeView === 'all' ? {
              pageSize: isMobile ? 10 : 20,
              showSizeChanger: !isMobile,
              showTotal: (total) => `Total: ${total} productos`,
            } : false}
            size="small"
            scroll={{ x: isMobile ? 600 : undefined }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductAnalysis;
